import { useCallback, useEffect, useState } from "react";
import {
  Drawer,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from "@mui/material";
import { format } from "date-fns";
import intl from "react-intl-universal";
import QRCode from "react-qr-code";
import {
  TbCopy,
  TbDotsVertical,
  TbLink,
  TbReceipt,
  TbRefresh,
  TbTrash,
  TbX,
} from "react-icons/tb";
import { useToast } from "../../../../../Hooks/toast";
import {
  cancelPixCharge,
  fetchPixChargeDetail,
  refreshPixChargeStatus,
} from "../../../../../Services/pixChargesApi";
import { formatCurrency } from "../../../../../Utils/formatters";
import { getPixChargeStatusMeta, getPixChargeTypeMeta } from "../pixChargesFilters";

const DetailMeta = ({ label, value, mono = false }) => (
  <div>
    <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">
      {label}
    </p>
    <p
      className={`mt-1 text-sm font-semibold text-brand-dark dark:text-gray-100 ${
        mono ? "break-all font-mono text-xs" : ""
      }`}
    >
      {value || "—"}
    </p>
  </div>
);

const formatDateTime = (value) => {
  if (!value) return "—";
  try {
    return format(new Date(value), "dd/MM/yyyy 'às' HH:mm");
  } catch {
    return "—";
  }
};

export const PixChargeDetailDrawer = ({
  open,
  charge: initialCharge,
  onClose,
  onChargeUpdated,
}) => {
  const { addToast } = useToast();
  const [charge, setCharge] = useState(initialCharge);
  const [loading, setLoading] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);

  useEffect(() => {
    setCharge(initialCharge);
  }, [initialCharge]);

  const copyText = useCallback(
    async (text, successKey) => {
      if (!text) return;
      try {
        await navigator.clipboard.writeText(text);
        addToast({
          type: "success",
          title: intl.get("pix.charges.copySuccess"),
          description: intl.get(successKey),
        });
      } catch {
        addToast({
          type: "error",
          title: intl.get("pix.charges.copyError"),
        });
      }
    },
    [addToast],
  );

  const handleRefresh = async () => {
    if (!charge?.id) return;
    setLoading(true);
    const result = await refreshPixChargeStatus(charge.id);
    setLoading(false);
    if (result.success) {
      setCharge(result.data);
      onChargeUpdated?.(result.data);
      addToast({
        type: "success",
        title: intl.get("pix.charges.refreshSuccess"),
      });
    } else {
      addToast({
        type: "error",
        title: result.message || intl.get("pix.charges.refreshError"),
      });
    }
  };

  const handleCancel = async () => {
    if (!charge?.id) return;
    setMenuAnchor(null);
    setLoading(true);
    const result = await cancelPixCharge(charge.id);
    setLoading(false);
    if (result.success) {
      setCharge(result.data);
      onChargeUpdated?.(result.data);
      addToast({
        type: "success",
        title: intl.get("pix.charges.cancelSuccess"),
      });
    } else {
      addToast({
        type: "error",
        title: result.message || intl.get("pix.charges.cancelError"),
      });
    }
  };

  useEffect(() => {
    if (!open || !initialCharge?.id) return;
    let cancelled = false;

    const loadDetail = async () => {
      setLoading(true);
      const result = await fetchPixChargeDetail(initialCharge.id);
      if (!cancelled) {
        setLoading(false);
        if (result.success) {
          setCharge(result.data);
        }
      }
    };

    loadDetail();
    return () => {
      cancelled = true;
    };
  }, [open, initialCharge?.id]);

  if (!charge) return null;

  const statusMeta = getPixChargeStatusMeta(charge.status);
  const typeMeta = getPixChargeTypeMeta(charge.type);
  const canCancel = charge.status === "waiting" || charge.status === "expired";
  const canViewReceipt = charge.status === "paid" || charge.status === "returned";

  const menuItems = [
    {
      id: "copyLink",
      labelKey: "pix.charges.actionCopyLink",
      icon: TbLink,
      onClick: () => {
        setMenuAnchor(null);
        copyText(charge.paymentLink, "pix.charges.copyLinkSuccess");
      },
      visible: Boolean(charge.paymentLink),
    },
    {
      id: "copyPix",
      labelKey: "pix.charges.actionCopyPix",
      icon: TbCopy,
      onClick: () => {
        setMenuAnchor(null);
        copyText(charge.qrcode, "pix.charges.copyPixSuccess");
      },
      visible: Boolean(charge.qrcode),
    },
    {
      id: "refresh",
      labelKey: "pix.charges.actionRefresh",
      icon: TbRefresh,
      onClick: () => {
        setMenuAnchor(null);
        handleRefresh();
      },
      visible: true,
    },
    {
      id: "cancel",
      labelKey: "pix.charges.actionCancel",
      icon: TbTrash,
      onClick: handleCancel,
      visible: canCancel,
      destructive: true,
    },
    {
      id: "receipt",
      labelKey: "pix.charges.actionViewReceipt",
      icon: TbReceipt,
      onClick: () => {
        setMenuAnchor(null);
        addToast({
          type: "info",
          title: intl.get("pix.charges.receiptSoon"),
        });
      },
      visible: canViewReceipt,
    },
  ].filter((item) => item.visible);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        className: "w-full max-w-md",
        sx: { width: { xs: "100%", sm: 420 } },
      }}
    >
      <div className="flex h-full flex-col">
        <header className="flex items-start justify-between gap-3 border-b border-gray-100 px-4 py-4 dark:border-brand-dark-border">
          <div className="min-w-0">
            <h2 className="text-base font-bold text-brand-dark dark:text-gray-100">
              {intl.get("pix.charges.detailTitle")}
            </h2>
            <div className="mt-2 flex flex-wrap gap-2">
              <span
                className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${statusMeta.className}`}
              >
                {statusMeta.label}
              </span>
              <span
                className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${typeMeta.className}`}
              >
                {typeMeta.label}
              </span>
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-1">
            <IconButton
              size="small"
              onClick={handleRefresh}
              disabled={loading}
              aria-label={intl.get("pix.charges.actionRefresh")}
            >
              <TbRefresh className={`h-5 w-5 ${loading ? "animate-spin" : ""}`} />
            </IconButton>
            <IconButton
              size="small"
              onClick={(event) => setMenuAnchor(event.currentTarget)}
              aria-label={intl.get("pix.charges.quickActions")}
            >
              <TbDotsVertical className="h-5 w-5" />
            </IconButton>
            <IconButton size="small" onClick={onClose} aria-label={intl.get("words.close")}>
              <TbX className="h-5 w-5" />
            </IconButton>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto px-4 py-5">
          <div className="flex flex-col items-center text-center">
            {charge.qrcode ? (
              <div className="rounded-xl border border-gray-100 bg-white p-4 dark:border-brand-dark-border dark:bg-white">
                <QRCode value={charge.qrcode} size={180} />
              </div>
            ) : null}
            <p className="mt-4 text-2xl font-bold text-brand-dark dark:text-gray-50">
              {formatCurrency(charge.amount)}
            </p>
          </div>

          {charge.qrcode ? (
            <div className="mt-6">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
                {intl.get("pix.charges.pixCopyPaste")}
              </p>
              <div className="flex items-start gap-2 rounded-xl border border-gray-100 bg-gray-50/80 p-3 dark:border-brand-dark-border dark:bg-brand-dark-elevated/60">
                <p className="min-w-0 flex-1 break-all font-mono text-xs text-gray-600 dark:text-gray-300">
                  {charge.qrcode}
                </p>
                <button
                  type="button"
                  className="btn-outline-icon shrink-0"
                  onClick={() => copyText(charge.qrcode, "pix.charges.copyPixSuccess")}
                  aria-label={intl.get("pix.charges.actionCopyPix")}
                >
                  <TbCopy className="h-4 w-4" />
                </button>
              </div>
            </div>
          ) : null}

          <section className="mt-6 rounded-xl border border-gray-100 bg-gray-50/60 p-4 dark:border-brand-dark-border dark:bg-brand-dark-elevated/60">
            <h3 className="mb-3 text-[11px] font-bold uppercase tracking-wider text-gray-400">
              {intl.get("pix.charges.history")}
            </h3>
            <div className="grid gap-3 sm:grid-cols-2">
              <DetailMeta label={intl.get("pix.charges.createdAt")} value={formatDateTime(charge.createdAt)} />
              <DetailMeta label={intl.get("pix.charges.expiresAt")} value={formatDateTime(charge.expiresAt)} />
              {charge.paidAt ? (
                <DetailMeta label={intl.get("pix.charges.paidAt")} value={formatDateTime(charge.paidAt)} />
              ) : null}
            </div>
          </section>

          <section className="mt-4 rounded-xl border border-gray-100 bg-gray-50/60 p-4 dark:border-brand-dark-border dark:bg-brand-dark-elevated/60">
            <h3 className="mb-3 text-[11px] font-bold uppercase tracking-wider text-gray-400">
              {intl.get("pix.charges.identifiers")}
            </h3>
            <div className="grid gap-3">
              <DetailMeta label={intl.get("pix.charges.txid")} value={charge.txid} mono />
              <DetailMeta label={intl.get("pix.charges.chargeId")} value={charge.chargeId || charge.id} mono />
            </div>
          </section>
        </div>

        {charge.paymentLink ? (
          <footer className="border-t border-gray-100 p-4 dark:border-brand-dark-border">
            <button
              type="button"
              className="btn-primary w-full"
              onClick={() => copyText(charge.paymentLink, "pix.charges.copyLinkSuccess")}
            >
              <TbLink className="h-4 w-4" />
              {intl.get("pix.charges.copyPaymentLink")}
            </button>
          </footer>
        ) : null}
      </div>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ className: "app-card", sx: { minWidth: 220, mt: 1 } }}
      >
        {menuItems.map((item) => {
          const Icon = item.icon;
          return (
            <MenuItem
              key={item.id}
              onClick={item.onClick}
              sx={{
                color: item.destructive ? "error.main" : "text.primary",
                py: 1,
              }}
            >
              <ListItemIcon sx={{ minWidth: 32, color: "inherit" }}>
                <Icon className="h-4 w-4" />
              </ListItemIcon>
              <ListItemText primary={intl.get(item.labelKey)} />
            </MenuItem>
          );
        })}
      </Menu>
    </Drawer>
  );
};
